import { Button } from "@/components/Button";
import { InsuranceQuoteContext } from "@/components/InsuranceQuoteLayout";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/Spinner";
import { Modal } from "@/components/Dialog";
import useLivlyUser from "@/context/UserProvider";
import { AssurantQuoteV2 } from "@/types/Insurance";

import React from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router";
import {
  useDeleteInsuranceV2Quote,
  useRequoteInsuranceV2Quote,
  useUpdateInsuranceV2Quote
} from "./insurance-quote-v2";
import { User } from "@/types/User";
import UnderWritingQuestions from "./under-writing-questions";
import LibalityQuestion300K from "./liability-questions-300K";

export interface UnderwritingQuestions {
  questionName: string;
  question: string;
  helpText: string;
  allowedResponses: string[];
  instructions: string;
}
export interface LibalityQuestionProps {
  user: User;
  onSubmit: (
    questions: {
      questionName: string;
      answer: string;
    }[]
  ) => Promise<void>;
  isSubmitting: boolean;
  isFetching: boolean;
  isInsuranceOnly: boolean;
}

export interface LiabilityQuestionItemsProps {
  question: UnderwritingQuestions;
  questionIndex: number;
  answers: Record<string, string>;
  onAnswerChange: (questionName: string, value: string | Date | null) => void;
}

interface LiabilityQuestionAnswer {
  questionName: string;
  answer: string;
}

function getValue(
  data: any[],
  paramName: string
): string | number | number[] | null | undefined {
  return data.find((p) => p.name === paramName)?.value;
}

const LiabilityQuestions = () => {
  const location = useLocation() as {
    state?: { label: string; isInsuranceOnly: boolean };
  };
  const { user } = useLivlyUser();
  const navigate = useNavigate();
  const { data, selectionMetaData, updateMetaData, isFetching } =
    useOutletContext<InsuranceQuoteContext>();

  const { parameters } = (data as AssurantQuoteV2).quoteResponse
    .coverageDetails;
  const liabilityAmount = getValue(parameters, "liabilityAmount");
  const deleteAssuranceQuoteMutation = useDeleteInsuranceV2Quote(user);
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);
  const requoteMutationV2 = useRequoteInsuranceV2Quote(user);
  const [showFreshQuoteModal, setShowFreshQuoteModal] = React.useState(false);
  const [freshQuoteMessage, setFreshQuoteMessage] = React.useState("");
  const [cachePayload, setCachePayload] = React.useState<
    LiabilityQuestionAnswer[]
  >([]);
  const handleGetFreshQuote = async () => {
    const v2 = data as AssurantQuoteV2;

    // You may want to call a function to get a fresh quote here BE pending
    await deleteAssuranceQuoteMutation.mutateAsync();
    const payload = {
      NewRequest: {
        ...v2.quoteRequest,
        underWriting: { questions: cachePayload }
      },
      SelectionMetaData: v2?.selectionMetaData
    };
    await updateAssurantQuoteMutation.mutateAsync(payload, {
      onSuccess: async (data) => {
        setCachePayload([]);
        const responseMessage =
          data.quoteResponse.transactionDetails.responseMessage;
        if (responseMessage) {
          navigate(`../insurance-quote/not-qualified`);
        } else {
          const payload = {
            ...data.quoteRequest,
            transaction: {
              ...data.quoteRequest.transaction,
              workItemId: data.quoteResponse.transactionDetails.workItemId
            }
          };
          await requoteMutationV2.mutateAsync(payload);
          if (location.state?.isInsuranceOnly) {
            navigate(`../insurance-quote/summary`, {
              state: { label: location.state.label, isInsuranceOnly: true }
            });
          } else {
            navigate(`../insurance-quote/summary`);
          }
        }
      }
    });
  };

  const onSubmit = async (
    questions: {
      questionName: string;
      answer: string;
    }[]
  ) => {
    const quoteRequest = (data as AssurantQuoteV2).quoteRequest;
    // Merge in any missing key/values from quoteRequest.underWriting.questions
    const existingQuestionNames = new Set(questions.map((q) => q.questionName));
    const additionalQuestions = (
      quoteRequest.underWriting.questions || []
    ).filter((q: any) => !existingQuestionNames.has(q.questionName));
    const mergedQuestions: LiabilityQuestionAnswer[] = [
      ...questions,
      ...additionalQuestions
    ];

    const payload = {
      NewRequest: {
        ...quoteRequest,
        transaction: {
          ...quoteRequest.transaction,
          referralId: Math.floor(
            1000000000 + Math.random() * 9000000000
          ).toString(), // 10-digit random
          workItemId: null
        },
        underWriting: { questions: mergedQuestions }
      },
      SelectionMetaData: (data as AssurantQuoteV2).selectionMetaData
    };

    setCachePayload(mergedQuestions);
    await deleteAssuranceQuoteMutation.mutateAsync();

    await updateAssurantQuoteMutation.mutateAsync(payload, {
      onSuccess: async (data) => {
        const responseMessage =
          data.quoteResponse.transactionDetails.responseMessage;
        if (
          responseMessage &&
          responseMessage.toLowerCase().includes("get a fresh quote")
        ) {
          setFreshQuoteMessage(responseMessage);
          setShowFreshQuoteModal(true);
        } else if (responseMessage) {
          setCachePayload([]);
          navigate(`../insurance-quote/not-qualified`, {
            state: {
              denialNotice: data.quoteResponse.transactionDetails.denialNotice
            }
          });
        } else {
          updateMetaData({ ...selectionMetaData!, uwAnswers: "" });
          setCachePayload([]);
          const payload = {
            ...data.quoteRequest,
            transaction: {
              ...data.quoteRequest.transaction,
              workItemId: data.quoteResponse.transactionDetails.workItemId
            }
          };
          await requoteMutationV2.mutateAsync(payload);

          if (location.state?.isInsuranceOnly) {
            navigate(`../insurance-quote/summary`, {
              state: { label: location.state.label, isInsuranceOnly: true }
            });
          } else {
            navigate(`../insurance-quote/summary`);
          }
        }
      },
      onError: () => {
        navigate(`../insurance-quote/not-qualified`);
      }
    });
  };
  return (
    <Layout
      title="Liability Questions"
      back={{
        label: "Insurance",
        to: `../insurance-quote`
      }}
    >
      <Modal
        open={showFreshQuoteModal}
        onClose={() => {}}
        hideClose
        title="Action Required"
      >
        <div className="p-4">
          <p className="mb-4">{freshQuoteMessage}</p>
          <Button
            onClick={handleGetFreshQuote}
            disabled={
              deleteAssuranceQuoteMutation.isLoading ||
              requoteMutationV2.isLoading ||
              updateAssurantQuoteMutation.isLoading
            }
          >
            {deleteAssuranceQuoteMutation.isLoading ||
            requoteMutationV2.isLoading ||
            updateAssurantQuoteMutation.isLoading ? (
              <Spinner size="sm" />
            ) : null}
            {deleteAssuranceQuoteMutation.isLoading ||
            requoteMutationV2.isLoading ||
            updateAssurantQuoteMutation.isLoading
              ? "Getting Fresh Quote..."
              : "Get a Fresh Quote"}
          </Button>
        </div>
      </Modal>
      <div className="max-w-4xl mx-auto">
        {Number(liabilityAmount) < 300000 ? (
          <UnderWritingQuestions
            user={user}
            onSubmit={onSubmit}
            isSubmitting={
              updateAssurantQuoteMutation.isLoading ||
              deleteAssuranceQuoteMutation.isLoading ||
              requoteMutationV2.isLoading
            }
            isFetching={isFetching}
            isInsuranceOnly={location.state?.isInsuranceOnly || false}
          />
        ) : (
          <LibalityQuestion300K
            initialUrl={
              (data as AssurantQuoteV2).quoteResponse.coveragesCue
                .businessRules[0].rule300KLiability.initialUrl ?? ""
            }
            outcomeUrl={
              (data as AssurantQuoteV2).quoteResponse.coveragesCue
                .businessRules[0].rule300KLiability.outcomeUrl ?? ""
            }
            isInsuranceOnly={location.state?.isInsuranceOnly || false}
            isFetching={isFetching}
          />
        )}
      </div>
    </Layout>
  );
};

export default LiabilityQuestions;
